export interface IService {
  id: string;
  title: string;
  description?: string | undefined;
  price?: number | undefined;
  isPublic?: number | undefined;
  isDisabled?: boolean | undefined;
  categoryId?: string | undefined;
  ownerId?: string | undefined;
  mainImageUrl: string;
  images?: string[] | undefined;
  usages?: number | undefined;
  rateCount?: number | undefined;
  rate?: number | undefined;
  averageTime?: number | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;

  isLiked?: boolean | undefined;
  isBlocked?: boolean | undefined;
  isActive?: boolean | undefined;
  likeCount?: number;

  owner?: IUser | undefined;
  category?: IServiceCategory | undefined;
  views?: number;
}

export interface IServiceCategory {
  id: string;
  title: string;
}

export interface IServiceConnection {
  items?: [IService] | undefined;
  totalCount?: number | undefined;
}

export interface IServiceFilters {
  searchString?: string;
  online?: boolean;
  ownerStatus?: UserStatus;
  categories?: string[];
  gender?: Gender;
  age?: string[];
  prefectures?: string[];
  isLiked?: boolean;
  isBlocked?: boolean;
  isDisabled?: boolean;
  ownerIds?: string[];
  excludeIds?: string[];
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
  UNDEFINED = "UNDEFINED",
}

export interface IUser {
  id: string;
  name?: string;
  image?: string;
  gender?: Gender;
  birthday?: string;
  address?: {
    postnumber?: string;
    prefecture?: string;
    city?: string;
    address?: string;
    address2?: string;
    address3?: string;
  };
  aboutTalk?: string;
  aboutProfile?: string;
  description?: string;
  voice?: string;
  minutesFromLastLogin?: number;

  identificationStatus: IdentificationStatus;
  identificationMessage: String;
  identificationDocuments: String;

  isBlocked?: Boolean;
  isOwnBlocked?: Boolean;
  rateCount?: number;
  rate?: number;
  createdAt?: string;
  lastLoginAt?: string;
  status?: UserStatus;
}

export enum UserStatus {
  LOGOUT = "LOGOUT",
  OFFERABLE = "OFFERABLE",
  ACTIVE = "ACTIVE",
  IN_CALL = "IN_CALL",
}

export interface IUserConnection {
  items?: [IUser] | undefined;
  totalCount?: number | undefined;
}

export interface IReview {
  id: string;
  serviceId?: string | undefined;
  userId?: string | undefined;
  ownerId?: string | undefined;
  requestId?: string | undefined;
  rate?: number | undefined;
  comment?: string | undefined;
  createdAt?: string | undefined;

  owner?: IUser | undefined;
  service?: IService | undefined;
}

export interface IReviewConnection {
  items?: [IReview];
  totalCount?: number;
}

export interface IAccount {
  id: string;
  email: string;
  name: string;
  image?: string;
  fullName?: string;
  gender?: Gender;
  birthday?: string;
  address?: IAddress;
  aboutTalk?: string;
  balance: number;
  sales: number;
  aboutProfile?: string;
  description: string;
  voice?: string;
  phone?: string;
  stipeCard?: string;
  stipeExpired?: string;
  bank?: IBankAccount;
  identificationStatus?: IdentificationStatus;
  identificationMessage?: string;
  identificationDocuments?: string;
  isDisabled?: boolean;
}

export interface IBankAccount {
  bankCode?: string;
  bankName?: string;
  branchCode?: string;
  branchName?: string;
  accountType?: string;
  accountHolder?: string;
  accountNumber?: string;
}

export enum IdentificationStatus {
  PROCESSED = "PROCESSED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface IAddress {
  postnumber?: string;
  prefecture?: string;
  city?: string;
  address?: string;
  address2?: string;
  address3?: string;
}

export interface IUserFilters {
  isBlocked?: boolean;
}

export enum ServiceOrder {
  SERVICE_UPDATED = "SERVICE_UPDATED",
  SERVICE_USAGE_COUNT = "SERVICE_USAGE_COUNT",
  SERVICE_RECOMMENDATION = "SERVICE_RECOMMENDATION",
}

export interface INotification {
  id: string;
  userId?: string;
  title?: string;
  details?: string;
  isUnread?: boolean;
  createdAt: string;
}

export interface INotificationConnection {
  items?: INotification[];
  totalCount?: number;
}

export interface IRequestConnection {
  items: IRequest[];
}

export enum RequestStatus {
  WAITING_OPPONENT = "WAITING_OPPONENT",
  APPROVED_BY_OPPONENET = "APPROVED_BY_OPPONENET",
  CANCELED_BY_SENDER = "CANCELED_BY_SENDER",
  REJECTED_BY_OPPONENT = "REJECTED_BY_OPPONENT",
  CALL_APPROVED = "CALL_APPROVED",
  CALL_REJECTED = "CALL_REJECTED",
  CALL_FINISHED = "CALL_FINISHED",
  EXPIRED = "EXPIRED",
  REOFFERED = "REOFFERED",
}

export interface IRequest {
  id: string;
  senderId: string;
  opponentId: string;
  serviceId: string;
  status: RequestStatus;
  waitingTime: number;
  price: number;
  message?: string;
  offerRejectMessage?: string;
  offerCancelMessage?: string;
  createdAt: string;
  startedAt?: string;
  rejectedAt?: string;
  finishedAt?: string;
  expiredAt?: string;
  freeTime?: number;
  maxTalkingTimeBefore?: string;

  service?: IService;
  sender?: IUser;
  opponent?: IUser;

  senderReview?: IReview;
  opponentReview?: IReview;
}

export interface IConversationMessageContent {
  text?: string;
  image?: string;
}

export interface IConversationMessage {
  id: string;
  conversationId: string;
  ownerId: string;
  opponentId: string;
  content?: IConversationMessageContent;
  createdAt?: string;
  readAt?: string;
}

export interface IPreConversation {
  id: string;
  senderId: string;
  opponentId: string;
  serviceId: string;
  updatedAt: string;

  service?: IService;
  sender?: IUser;
  opponent?: IUser;
  lastReceivedMessage?: IConversationMessage;
  lastMessage?: IConversationMessage;
}

export interface IPreConversationConnection {
  items?: IPreConversation[];
  totalCount?: number;
}

export interface IServiceCreateInput {
  id: string;
  title: string;
  description: string;
  price: number;
  isPublic?: number;
  categoryId: string;
  mainImageUrl: string;
  images: string[];
}

export interface IServiceUpdateInput {
  id: string;
  title: string;
  description: string;
  price: number;
  isPublic?: number;
  categoryId: string;
  mainImageUrl: string;
  images: string[];
}

export interface IRequestCreateInput {
  id: string;
  serviceId: string;
  waitingTime: number;
  message?: string;
  requestTimestamp?: string;
}

export interface IConversationMessageConnection {
  items?: IConversationMessage[];
  nextToken?: string;
}

export interface IConversationMessageInput {
  id: string;
  conversationId: string;
  opponentId: string;
  text?: string;
  image?: string;
}

export interface IConversationReviewInput {
  id: string;
  requestId: string;
  userId?: string;
  serviceId?: string;
  rate: number;
  comment?: string;
}

export interface IPreConversationInput {
  id: string;
  opponentId: string;
  serviceId: string;
}

export enum SalesType {
  WITHDRAWAL_REQUEST = "WITHDRAWAL_REQUEST",
  WITHDRAWAL_APPROVED = "WITHDRAWAL_APPROVED",
  WITHDRAWAL_REJECTED = "WITHDRAWAL_REJECTED",
  WITHDRAWAL_PROCESSING = "WITHDRAWAL_PROCESSING",
  WITHDRAWAL_ERROR = "WITHDRAWAL_ERROR",
  SALES = "SALES",
}

export enum SalesBankStatus {
  UNCHECKED = "UNCHECKED",
  CHECKED = "CHECKED",
  REJECTED = "REJECTED",
}

export interface ISales {
  id: string;
  userId: string;
  requestId: string;
  type: SalesType;

  amount?: number;
  requestAmount?: number;
  systemFee?: number;
  validationFee?: number;
  approveFee?: number;
  rejectFee?: number;
  errorFee?: number;
  processingFee?: number;
  transferAmount?: number;

  transferAt?: string;
  createdAt: string;
  updatedAt?: string;

  bankCode?: string;
  bankName?: string;
  bankBranchCode?: string;
  bankBranchName?: string;
  bankAccountType?: string;
  bankAccountHolder?: string;
  bankAccountNumber?: string;

  bankStatus?: SalesBankStatus;

  memo?: string;

  request?: IRequest;
  user?: IUser;
}

export interface IWithdrawalInput {
  amount: number;
  bankCode: string;
  bankName: string;
  branchCode: string;
  branchName: string;
  accountType: string;
  accountHolder: string;
  accountNumber: string;
}

export interface ISalesConnection {
  items?: ISales[];
  totalCount?: number;
  nextToken?: string;
}

export interface IAccountUpdateInput {
  name: string;
  image?: string;
  fullName?: string;
  gender?: Gender;
  birthday?: string;
  addressPostnumber?: string;
  addressPrefecture?: string;
  addressCity?: string;
  address?: string;
  address2?: string;
  address3?: string;
  aboutTalk?: string;
  aboutProfile?: string;
  description?: string;
  voice?: string;
  bankCode?: string;
  bankName?: string;
  bankBranchCode?: string;
  bankBranchName?: string;
  bankAccountType?: string;
  bankAccountHolder?: string;
}

export interface IAccountUpdateIdentificationInput {
  identificationStatus?: IdentificationStatus;
  identificationMessage?: string;
  identificationDocuments?: string;
}

export enum TransactionType {
  BUY = "BUY",
  FREE = "FREE",
  USE = "USE",
}

export interface ITransaction {
  id: string;
  userId: string;
  type: TransactionType;
  amount?: number;
  createdAt: string;
}

export interface ITransactionConnection {
  items?: ITransaction[];
  totalCount?: number;
}

export interface ICreditCardInput {
  number: string;
  expiryMonth: number;
  expiryYear: number;
  cvc: string;
}

export interface IBank {
  id: string;
  name?: string;
  kana?: string;
  hira?: string;
  roma?: string;

  branches?: IBankBranch[];
}

export interface IBankBranch {
  id: string;
  code: string;
  bankId: string;
  name?: string;
  kana?: string;
  hira?: string;
  roma?: string;
}
